import { effect, signal } from "@preact/signals";
import { type ComponentChildren, createPortal, Fragment, type JSX, type TargetedSubmitEvent, type TargetedToggleEvent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { FILE_ACCEPT, PHOTO_ACCEPT } from "../../../shared/mime.ts";
import { type Card, LIMITS } from "../../../shared/types.ts";
import { openLocal } from "../data/db.ts";
import { clearLocalData } from "../data/index.ts";
import { textDoc } from "../data/transfer.ts";
import { Detail, stepCard } from "./Detail.tsx";
import { Caption, focusNote, Frame, Grid, ImageRetry, MOD, QuickNote, TOUCH } from "./Grid.tsx";
import {
  IconAlert, IconCheck, IconChevronUp, IconClose, IconCompass, IconCursorCard, IconGear, IconGrid2, IconGrid3, IconGrid4, IconGridSolid, IconKeyboard,
  IconLogout, IconMasonry, IconMenu, IconMoon, IconOffline, IconPlus, IconPrev, IconRing, IconSpin, IconSun, IconTrash, IconUp, Mark,
} from "./Icons.tsx";
import { absoluteTime, cardText, DENSITY, type Density, isTypingTarget, KIND_LABEL, linkFromInput, shortcutFor, syncView, unsentWarning, urlFromText } from "./lib.ts";
import { AccountPage } from "./pages/AccountPage.tsx";
import { ApiPage } from "./pages/ApiPage.tsx";
import { DevicesPage } from "./pages/DevicesPage.tsx";
import { SerendipityPage } from "./pages/SerendipityPage.tsx";
import { SpacesPage } from "./pages/SpacesPage.tsx";
import { TagsPage } from "./pages/TagsPage.tsx";
import { PinsStrip } from "./pins/PinsStrip.tsx";
import { focusSearch, Search } from "./Search.tsx";
import { CapturedSheet } from "./share/Captured.tsx";
import { IconPhone, IconPuzzle, IconShareSheet } from "./share/icons.tsx";
import { consumeShares, importNoteFiles, isNoteFile } from "./share/inbox.ts";
import { BatchBar } from "./spaces/BatchBar.tsx";
import { IconSelect, IconTag } from "./spaces/icons.tsx";
import { selecting } from "./spaces/selection.ts";
import { SaveSearch } from "./spaces/SpaceChips.tsx";
import { backTo, chips, density, dismiss, failed, go, load, me, meaning, modal, query, refocus, route, save, showModal, store, text, theme, toast, toasts, visible, width, type Me, type Theme, type Toast } from "./state.ts";

const ME_KEY = "faig.me";
/** Rede devagar ao abrir (Wi-Fi de portaria): depois disso o app sobe com a última conta e os dados do aparelho. */
const ME_TIMEOUT_MS = 4000;
/** A sessão deste aparelho terminou (havia conta guardada): a entrada avisa que o que está aqui continua aqui. */
const ended = signal(false);

/**
 * Troca de conta: o cookie virou de outra conta (código ou Google de outra pessoa, folha do Google no app instalado) e o
 * servidor recusou a réplica desta (account_mismatch), então nada atravessa. O app reabre e o boot abre a outra conta com
 * o banco dela; a fila desta fica no banco desta e sobe quando ela entrar de novo. LEFT_KEY lembra quem deixou itens na
 * fila deste aparelho (só sair apaga, e o aviso de sair conta esses itens). PREV_KEY: a conta de antes de a sessão vencer.
 */
const LEFT_KEY = "faig.left";
const PREV_KEY = "faig.prev";
type Left = Me & { pending: number };
const parse = <T,>(key: string, fallback: T): T => {
  try {
    return (JSON.parse(load(key) ?? "null") as T | null) ?? fallback;
  } catch {
    return fallback;
  }
};
const left = signal<Left[]>(parse<Left[]>(LEFT_KEY, []));
const setLeft = (v: Left[]) => ((left.value = v), save(LEFT_KEY, v.length ? JSON.stringify(v) : null));
const items = (n: number) => (n === 1 ? "1 item" : `${n} itens`);
let switching = false;
effect(() => {
  if (store.status.value.error !== "account_mismatch" || switching) return;
  switching = true;
  void store.stop().catch(() => {}).finally(() => location.reload());
});

/** Entrou numa conta diferente da última deste aparelho: guarda quantos itens a anterior deixou na fila e avisa. */
async function switched(prev: Me, m: Me) {
  let pending = 0;
  try {
    const d = await openLocal(prev.id);
    pending = await d.count("outbox");
    d.close();
  } catch {}
  const rest = [...left.peek().filter((l) => l.id !== m.id && l.id !== prev.id), ...(pending ? [{ ...prev, pending }] : [])];
  setLeft(rest);
  const n = rest.reduce((t, l) => t + l.pending, 0);
  const waiting = rest.map((l) => `${items(l.pending)} de ${l.email}`).join(" e ");
  const tail = n ? ` ${waiting} ${n === 1 ? "espera" : "esperam"} neste aparelho e ${n === 1 ? "sobe" : "sobem"} quando você entrar com ${rest.length === 1 ? "essa conta" : "essas contas"}.` : "";
  toast({ key: "switched", text: `Você entrou como ${m.email}.${tail}`, action: rest[0] && { label: "Entrar nela", run: () => relogin(rest[0]) } }, 15_000);
}

/** GET /v1/me: 401 → tela de entrada. Sem rede (ou rede lenta demais) → última conta deste aparelho (IndexedDB do store). */
async function boot() {
  const prev = parse<Me | null>(ME_KEY, null) ?? parse<Me | null>(PREV_KEY, null);
  let m: Me | null;
  try {
    const r = await fetch("/v1/me", { signal: AbortSignal.timeout?.(ME_TIMEOUT_MS) });
    if (r.status === 401) {
      if (load(ME_KEY)) ended.value = true;
      if (prev) save(PREV_KEY, JSON.stringify(prev));
      save(ME_KEY, null);
      me.value = null;
      return;
    }
    if (!r.ok) throw new Error(`me_${r.status}`);
    const body = (await r.json()) as Me;
    m = { id: body.id, email: body.email };
    save(ME_KEY, JSON.stringify(m));
    save(PREV_KEY, null);
  } catch {
    m = parse<Me | null>(ME_KEY, null);
  }
  me.value = m;
  if (m && prev && prev.id !== m.id) void switched(prev, m);
  else if (m && left.peek().some((l) => l.id === m.id)) setLeft(left.peek().filter((l) => l.id !== m.id)); // voltou: a fila sobe agora
  if (m) store.start(m.id).then(consumeShares).catch(failed);
}

/** App da Tela de Início (iPhone, Android): cookies próprios, e o Google abre numa folha à parte. */
const standalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
// iPadOS se apresenta como Mac: o toque com mais de um ponto o separa de um Mac de verdade.
const IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Volta ao primeiro plano (a folha do Google fechou, o app foi reaberto) ou a página voltou do cache: roda `fn`. */
function useWake(fn: () => void) {
  useEffect(() => {
    const wake = () => void (document.visibilityState === "visible" && fn());
    document.addEventListener("visibilitychange", wake);
    addEventListener("pageshow", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      removeEventListener("pageshow", wake);
    };
  }, []);
}

export async function logout(accountId: string) {
  const uploading = store.cards.value.filter((c) => c.ingestState === "uploading" || c.ingestError === "upload_refused").length;
  // Sair apaga o armazenamento do site inteiro (Clear-Site-Data): a fila de outra conta deste aparelho vai junto.
  const warn = unsentWarning(store.status.value.pending + left.value.reduce((t, l) => t + l.pending, 0), uploading);
  if (warn && !confirm(warn)) return;
  try {
    if (!(await fetch("/auth/logout", { method: "POST" })).ok) throw new Error("logout");
  } catch {
    return toast({ key: "logout", text: "Sem conexão: não deu para sair agora." });
  }
  save(ME_KEY, null);
  // Para o sync (fecha o banco) e apaga a réplica da conta; o Clear-Site-Data da resposta é a segunda garantia.
  await store.stop().catch(() => {});
  await clearLocalData(accountId).catch(() => {});
  location.reload();
}

function openModal(id: string) {
  const d = document.getElementById(id) as HTMLDialogElement | null;
  if (!d || d.open) return;
  const back = document.activeElement as HTMLElement | null;
  d.addEventListener("close", () => back?.focus(), { once: true });
  showModal(d);
  d.focus(); // foco na própria gaveta (tabindex -1), não no ×: sem anel ao abrir; Tab chega ao ×
}

function openNote() {
  chips.value = [];
  text.value = "";
  go("/");
  focusNote();
}

/**
 * Arquivos (vários): .md/.txt viram notas (importação); o resto vai ao createFile, que confere os bytes (imagem, PDF,
 * vídeo, áudio) e avisa uma vez por arquivo recusado. Uma URL vira card de link.
 */
function capture(dt: DataTransfer): boolean {
  const files = [...dt.files];
  if (files.length) {
    const notes = files.filter(isNoteFile);
    for (const f of files) if (!isNoteFile(f)) store.createFile(f).catch(failed);
    if (notes.length) importNoteFiles(notes);
    return true;
  }
  const uri = dt.getData("text/uri-list").split(/\r?\n/).find((l) => l && !l.startsWith("#"));
  const url = urlFromText(uri ?? dt.getData("text/plain"));
  if (!url) return false;
  store.createLink(url).catch(failed);
  return true;
}

/** Arquivo ou link sendo arrastado sobre a janela (a página esmaece e o leitor de tela ouve "Solte para guardar"). */
const dragging = signal(false);
effect(() => void document.documentElement.toggleAttribute("data-drag", dragging.value));

function useGlobalInput() {
  useEffect(() => {
    const modalOpen = () => !!document.querySelector("dialog[open]");
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const s = shortcutFor(e);
      if (!s) return;
      if (s === "prev" || s === "next") {
        if (!document.querySelector("dialog.detail[open]")) return;
        e.preventDefault();
        return stepCard(s === "next" ? 1 : -1);
      }
      if (modalOpen()) return;
      e.preventDefault();
      if (s === "note") openNote();
      else if (s === "search") focusSearch();
      else openModal("help");
    };
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented || isTypingTarget(e.target) || modalOpen() || !e.clipboardData) return;
      if (capture(e.clipboardData)) e.preventDefault();
    };
    const onDragOver = (e: DragEvent) => {
      const types = e.dataTransfer?.types ?? [];
      if (!types.includes("Files") && !types.includes("text/uri-list")) return;
      e.preventDefault();
      dragging.value = !modalOpen();
    };
    const onDragLeave = (e: DragEvent) => void (e.relatedTarget === null && (dragging.value = false));
    // Topo rolado: a busca vira pílula flutuante e o trilho troca o wordmark por "Voltar ao topo" (CSS).
    // data-moved: no compacto a barra fica presa e a linha de etiquetas some assim que a página anda.
    const onScroll = () => {
      document.documentElement.toggleAttribute("data-scrolled", scrollY > 100);
      document.documentElement.toggleAttribute("data-moved", scrollY > 4);
    };
    const onDrop = (e: DragEvent) => {
      dragging.value = false;
      if (e.defaultPrevented || !e.dataTransfer || modalOpen()) return;
      if (capture(e.dataTransfer)) e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("paste", onPaste);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    document.addEventListener("dragleave", onDragLeave);
    addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      document.removeEventListener("dragleave", onDragLeave);
      removeEventListener("scroll", onScroll);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, []);
}

export function App() {
  useEffect(() => void boot(), []);
  const m = me.value;
  if (m === undefined) return <p class="loading" aria-busy="true">Carregando…</p>;
  if (m === null) return <SignIn />;
  return <Shell me={m} />;
}

const Wordmark = () => (
  <>
    fai<span class="dot">.</span>g
  </>
);

function SignIn() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(navigator.onLine);
    addEventListener("online", on);
    addEventListener("offline", on);
    return () => (removeEventListener("online", on), removeEventListener("offline", on));
  }, []);
  // A sessão pode ter chegado por fora (a folha do Google fechou, outro jeito de entrar): confere ao voltar.
  useWake(() => void boot());
  return (
    <main class="signin">
      <span class="signin-mark" aria-hidden="true">
        <Mark />
      </span>
      <h1 class="wordmark">
        <Wordmark />
      </h1>
      <p>Links, imagens e notas guardados num lugar só, fáceis de achar de novo.</p>
      {ended.value && <p class="signin-note">Sua sessão terminou. O que está neste aparelho continua aqui e sobe quando você entrar.</p>}
      {!online && <p role="status">Sem conexão agora. Conecte-se à internet para entrar.</p>}
      <LoginChoices onCode={() => void boot()} />
    </main>
  );
}

/**
 * Entrar: Google (mesma aba) e, por baixo, o código. No app da Tela de Início do iPhone o Google abre numa folha com
 * cookies à parte e a sessão pode não voltar para o app; o código, gerado num navegador já dentro da conta, entra
 * por um fetch do próprio app e o cookie cai no pote dele.
 */
function LoginChoices({ onCode, codeFirst }: { onCode: () => void; codeFirst?: boolean }) {
  const [code, setCode] = useState(!!codeFirst);
  return (
    <div class="login">
      <a class="btn primary" href="/auth/google">
        Entrar com Google
      </a>
      {code ? (
        <CodeForm onDone={onCode} />
      ) : (
        <button type="button" class="text-btn login-code-btn" onClick={() => setCode(true)}>
          Entrar com código
        </button>
      )}
    </div>
  );
}

function CodeForm({ onDone }: { onDone: () => void }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);
  const submit = async (e: TargetedSubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = field.current!.value.replace(/\D/g, "");
    if (code.length !== 6) return setMsg("O código tem 6 números.");
    setBusy(true);
    setMsg("");
    try {
      const r = await fetch("/auth/code", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
      if (r.ok) return onDone();
      setMsg(r.status === 429 ? "Muitas tentativas. Espere um minuto e tente de novo." : "Código inválido, vencido ou já usado. Gere outro.");
    } catch {
      setMsg("Sem conexão. Tente de novo quando a rede voltar.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="code-form" onSubmit={submit}>
      <label for="login-code">Código de 6 números</label>
      <input ref={field} id="login-code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} aria-describedby="login-code-help" />
      <button class="btn primary" disabled={busy}>
        Entrar
      </button>
      <p class="code-msg" role="status">
        {msg}
      </p>
      <p id="login-code-help" class="code-help">
        Gere o código num navegador já dentro da conta (Safari ou computador): abra {location.host}/aparelhos e toque em “Criar código de entrada”. Vale por {LIMITS.pairingCodeMinutes} minutos.
      </p>
    </form>
  );
}

const nav = (path: string) => (e: MouseEvent) => {
  if (e.button || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  hide("menu-sheet");
  go(path);
};
const hide = (id: string) => document.getElementById(id)?.hidePopover();
const pickImages = () => document.getElementById("add-file")?.click();

function Shell({ me: m }: { me: Me }) {
  useGlobalInput();
  const path = route.value.path;
  const home = path === "/" || path === "" || path === "/capturado"; // /capturado: Tudo com a folha do compartilhamento
  return (
    <>
      <a
        class="skip"
        href="#main"
        onClick={(e) => {
          e.preventDefault(); // sem mexer no hash: ele é do detalhe
          document.getElementById("main")?.focus();
        }}
      >
        Pular para o conteúdo
      </a>
      <div class="topblur" aria-hidden="true" />
      <Rail path={path} />
      <header class="top">
        {!home && (
          // Celular: as outras páginas também têm o menu no topo, como o Tudo (no compacto ele é a quarta aba).
          <button type="button" class="page-menu" popovertarget="menu-sheet" aria-label="Menu">
            <IconMenu />
          </button>
        )}
        <div class="top-right">
          <SyncStatus />
          <Tabs path={home ? "/" : path} />
        </div>
        {home && (
          <>
            <Search
              lead={
                <button type="button" class="menu-btn" popovertarget="menu-sheet" aria-label="Menu">
                  <IconMenu />
                </button>
              }
            >
              <SaveSearch />
              <button type="button" class="add-btn" popovertarget="add-menu" aria-label="Adicionar" data-tip="Adicionar">
                <IconPlus />
              </button>
            </Search>
            <button type="button" class="add-square" popovertarget="add-menu" aria-label="Adicionar">
              <IconPlus />
            </button>
          </>
        )}
      </header>
      <Page path={path} />
      <AddMenu />
      <CapturedSheet />
      <RailPopovers me={m} />
      <MenuSheet me={m} />
      {(store.status.value.error === "unauthorized" || left.value.length > 0) && <Relogin />}
      <Detail />
      <Help />
      <Toasts />
      <p class="sr-only" aria-live="polite">
        {dragging.value ? "Solte para guardar" : ""}
      </p>
    </>
  );
}

const TABS: [string, string, () => JSX.Element][] = [
  ["/", "Tudo", IconMasonry],
  ["/colecoes", "Coleções", IconRing],
  ["/reencontro", "Reencontro", IconCompass],
];

function Tabs({ path }: { path: string }) {
  return (
    <nav class="tabs" aria-label="Seções">
      <ul>
        {TABS.map(([href, label, Icon]) => (
          <li key={href}>
            <a href={href} onClick={nav(href)} aria-current={path === href || (href !== "/" && path.startsWith(`${href}/`)) ? "page" : undefined}>
              <Icon />
              <span>{label}</span>
            </a>
          </li>
        ))}
        <li class="tab-menu">
          <button type="button" popovertarget="menu-sheet" aria-label="Menu">
            <IconMenu />
            <span aria-hidden="true">Menu</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

/** Trilho esquerdo (≥ 1025): wordmark girado e a pilha Atalhos · Tema · Lixeira · Densidade · Conta. */
function Rail({ path }: { path: string }) {
  return (
    <div class="rail">
      {/* Sinal + palavra; rolado, o chevron "Voltar ao topo" toma o lugar do sinal. */}
      <a class="rail-mark" href="/" onClick={nav("/")} aria-label="fai.g, início">
        <Mark />
        <span class="wordmark">
          <Wordmark />
        </span>
      </a>
      <button type="button" class="rail-top" aria-label="Voltar ao topo" onClick={() => scrollTo({ top: 0, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" })}>
        <IconChevronUp />
        <span class="wordmark" aria-hidden="true">
          <Wordmark />
        </span>
      </button>
      <ul class="rail-tools">
        <li>
          <button type="button" aria-label="Atalhos" data-tip="Atalhos" onClick={() => openModal("help")}>
            <IconKeyboard />
          </button>
        </li>
        <li>
          <button type="button" class="theme-btn" popovertarget="theme-pop" aria-label="Tema" data-tip="Tema">
            <span class="i-sun">
              <IconSun />
            </span>
            <span class="i-moon">
              <IconMoon />
            </span>
          </button>
        </li>
        <li>
          <a href="/lixeira" onClick={nav("/lixeira")} aria-label="Lixeira" data-tip="Lixeira" aria-current={path === "/lixeira" ? "page" : undefined}>
            <IconTrash />
          </a>
        </li>
        <li>
          <button type="button" popovertarget="density-pop" aria-label="Densidade" data-tip="Densidade">
            <IconGridSolid />
          </button>
        </li>
        <li>
          <button type="button" class="gear" popovertarget="account-pop" aria-label="Conta" data-tip="Conta">
            <IconGear />
          </button>
        </li>
      </ul>
    </div>
  );
}

const THEMES: [Theme, string][] = [
  ["light", "Claro"],
  ["dark", "Escuro"],
  ["system", "Sistema"],
];
const DENSITY_ICON: Record<Density, () => JSX.Element> = { amplo: IconGrid2, medio: IconGrid3, compacto: IconGrid4 };

const ThemePicker = () => (
  <div class="seg" role="group" aria-label="Tema">
    {THEMES.map(([t, label]) => (
      <button type="button" key={t} aria-pressed={theme.value === t} onClick={() => (theme.value = t)}>
        {label}
      </button>
    ))}
  </div>
);

/** Seletor de densidade; `icons` = só ícones (pílula vertical do trilho). */
const DensityPicker = ({ icons }: { icons?: boolean }) => (
  <div class="seg" role="group" aria-label="Densidade">
    {(Object.keys(DENSITY) as Density[]).map((d) => {
      const Icon = DENSITY_ICON[d];
      return (
        <button type="button" key={d} aria-pressed={density.value === d} aria-label={icons ? DENSITY[d].label : undefined} onClick={() => (density.value = d)}>
          {icons ? <Icon /> : DENSITY[d].label}
        </button>
      );
    })}
  </div>
);

function RailPopovers({ me: m }: { me: Me }) {
  return (
    <>
      <div id="theme-pop" popover="auto" class="pop dark-pop">
        <ThemePicker />
      </div>
      <div id="density-pop" popover="auto" class="pop dark-pop">
        <DensityPicker icons />
      </div>
      <div id="account-pop" popover="auto" class="pop menu">
        <p class="menu-email">{m.email}</p>
        <a class="menu-item" href="/conta" onClick={(e) => (hide("account-pop"), nav("/conta")(e))}>
          Conta e preferências
        </a>
        <button type="button" class="menu-item" onClick={() => logout(m.id)}>
          Sair
        </button>
      </div>
    </>
  );
}

/** Quem abriu o menu, o "+" ou o "Entrar de novo" (o foco volta para ele quando fecham sem trocar de página). */
let invoker: { el: HTMLElement | null; path: string } = { el: null, path: "" };
addEventListener(
  "click",
  (e) => {
    const t = (e.target as Element | null)?.closest?.<HTMLElement>("[popovertarget]");
    if (t && !t.closest("[popover]")) invoker.el = t; // o "Fechar" de dentro da folha não é quem a abriu
  },
  true,
);
/**
 * Folhas (popover): ao abrir, o foco vai para a própria folha (o leitor de tela lê o nome dela e segue dali, sem passar
 * pela grade inteira); ao fechar sem trocar de página, volta para quem abriu.
 */
function sheetFocus(e: TargetedToggleEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (e.newState === "open") {
    invoker = { el: invoker.el, path: location.pathname };
    return void requestAnimationFrame(() => el.focus({ preventScroll: true }));
  }
  const now = document.activeElement;
  if (location.pathname === invoker.path && (!now || now === document.body || el.contains(now)) && invoker.el?.isConnected) invoker.el.focus({ preventScroll: true });
}

/** Menu do celular e do compacto (≤ 1024, sem trilho): gaveta pela esquerda com conta, tema, densidade, lixeira e atalhos. */
function MenuSheet({ me: m }: { me: Me }) {
  return (
    <div id="menu-sheet" popover="auto" class="pop drawer" role="dialog" aria-label="Menu" tabIndex={-1} onToggle={sheetFocus}>
      <button type="button" class="drawer-close" popovertarget="menu-sheet" popovertargetaction="hide" aria-label="Fechar">
        <IconClose />
      </button>
      <p class="drawer-title">Que bom ter você por aqui.</p>
      <p class="menu-label">Sua conta</p>
      <p class="drawer-email">{m.email}</p>
      {left.value.map((l) => (
        <button type="button" key={l.id} class="row-pill drawer-switch" onClick={() => relogin(l)}>
          <span>
            Entrar com {l.email}
            <small>{items(l.pending)} na fila deste aparelho</small>
          </span>
        </button>
      ))}
      <p class="menu-label">Tema</p>
      <ThemePicker />
      <p class="menu-label">Densidade</p>
      <DensityPicker />
      <ul class="drawer-rows">
        <li>
          <a class="row-pill" href="/conta" onClick={nav("/conta")}>
            Conta e preferências
            <IconGear />
          </a>
        </li>
        <li>
          <a class="row-pill" href="/etiquetas" onClick={nav("/etiquetas")}>
            Etiquetas
            <IconTag />
          </a>
        </li>
        <li>
          <button type="button" class="row-pill" onClick={() => (hide("menu-sheet"), go("/"), (selecting.value = true))}>
            Selecionar cards
            <IconSelect />
          </button>
        </li>
        <li>
          <a class="row-pill" href="/lixeira" onClick={nav("/lixeira")}>
            Lixeira
            <IconTrash />
          </a>
        </li>
        <li>
          {TOUCH ? (
            // Tela de toque: no lugar dos atalhos de teclado, instalar o app e guardar pelo Compartilhar.
            <a class="row-pill" href={`/aparelhos?guia=${IOS ? "iphone" : "android"}`} onClick={nav(`/aparelhos?guia=${IOS ? "iphone" : "android"}`)}>
              {IOS ? "App e atalho do iPhone" : "App e compartilhar"}
              <IconPhone />
            </a>
          ) : (
            <button type="button" class="row-pill" onClick={() => (hide("menu-sheet"), openModal("help"))}>
              Atalhos de teclado
              <IconKeyboard />
            </button>
          )}
        </li>
        <li>
          <button type="button" class="row-pill" onClick={() => logout(m.id)}>
            Sair
            <IconLogout />
          </button>
        </li>
      </ul>
    </div>
  );
}

/** Uma rota → uma página; cada página nova vive no seu arquivo (pages/). */
function Page({ path }: { path: string }) {
  if (path === "/lixeira") return <Trash />;
  if (path === "/colecoes" || path.startsWith("/colecoes/")) return <SpacesPage path={path} />;
  if (path === "/reencontro") return <SerendipityPage />;
  if (path === "/etiquetas") return <TagsPage />;
  if (path === "/conta") return <AccountPage />;
  if (path === "/aparelhos") return <DevicesPage />;
  if (path === "/api-e-agentes") return <ApiPage />;
  return <Everything />;
}

function Everything() {
  const q = query.value;
  const list = visible.value;
  const dock = width.value <= 768;
  const s = store.status.value;
  return (
    <main id="main" tabIndex={-1} class="everything">
      <h1 class="sr-only">{q ? "Resultado da busca" : "Tudo"}</h1>
      {q && (
        <p class="result-count sr-only" role="status">
          {list.length === 1 ? "1 card" : `${list.length} cards`}
        </p>
      )}
      {!q && <PinsStrip />}
      <BatchBar />
      <Grid cards={list} lead={!q && !dock} resetKey={q} density={density.value} />
      {q && list.length === 0 && meaning.value !== "loading" && <Empty status title="Nada por aqui." line="Tente outra palavra ou tire um filtro." />}
      {!q && list.length === 0 && (s.lastSyncAt !== null || !s.online) && <FirstRun />}
      {!q && dock && <QuickNote dock />}
    </main>
  );
}

/** Dispensar o card-guia vale para este navegador (o e2e grava "done" para começar sem ele). */
const GUIDE_KEY = "faig.guide";

/** Tudo vazio (F-W1): card-guia com os três jeitos de guardar até ser dispensado; depois, o círculo de enviar imagens. */
function FirstRun() {
  const [guide, setGuide] = useState(() => load(GUIDE_KEY) !== "done");
  const done = () => {
    save(GUIDE_KEY, "done");
    setGuide(false);
    refocus(() => document.querySelector<HTMLElement>(".drop-circle"));
  };
  return (
    <Empty title="Nada guardado ainda." line={guide ? undefined : TOUCH ? "Toque em + para guardar uma foto, um link ou um arquivo, ou escreva uma nota abaixo." : "Cole um link, arraste uma imagem ou escreva uma nota."}>
      {guide ? (
        <Guide onDone={done} />
      ) : (
        <button type="button" class="drop-circle" aria-label="Escolher imagens" onClick={pickImages}>
          <IconUp />
        </button>
      )}
    </Empty>
  );
}

function Guide({ onDone }: { onDone: () => void }) {
  return (
    <section class="guide" aria-labelledby="guide-title">
      <h2 id="guide-title" class="guide-title">
        Três jeitos de guardar
      </h2>
      {TOUCH ? <TouchWays /> : <DesktopWays />}
      <button type="button" class="guide-done" onClick={onDone}>
        Entendi
      </button>
    </section>
  );
}

/** Celular e tablet: o "+", o Compartilhar de outros apps e o app na Tela de Início (sem colar, arrastar nem extensão). */
function TouchWays() {
  const share = IOS ? "/aparelhos?guia=iphone" : "/aparelhos?guia=android";
  return (
    <ol class="guide-ways">
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconPlus />
        </span>
        <div>
          <p class="guide-name">Pelo +</p>
          <p class="guide-line">Toque em + para tirar uma foto, guardar uma imagem, um arquivo ou um link, ou colar o que copiou. Para uma nota, escreva no campo de baixo: um link colado nele vira card de link.</p>
        </div>
      </li>
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconShareSheet />
        </span>
        <div>
          <p class="guide-name">Compartilhar de outro app</p>
          <p class="guide-line">
            {IOS ? "No iPhone, pelo atalho “Salvar no fai.g” na folha Compartilhar. " : "Com o fai.g instalado, ele aparece na folha Compartilhar. "}
            <a href={share} onClick={nav(share)}>
              Como ligar
            </a>
          </p>
        </div>
      </li>
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconPhone />
        </span>
        <div>
          <p class="guide-name">Instalar na Tela de Início</p>
          <p class="guide-line">
            Abra pelo ícone, como um app, e guarde até sem conexão.{" "}
            <a href={share} onClick={nav(share)}>
              Ver os passos
            </a>
          </p>
        </div>
      </li>
    </ol>
  );
}

function DesktopWays() {
  return (
    <ol class="guide-ways">
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconUp />
        </span>
        <div>
          <p class="guide-name">Colar ou arrastar</p>
          <p class="guide-line">
            Copie um link ou uma imagem e aperte {MOD}+V aqui, ou solte arquivos nesta janela.{" "}
            <button type="button" class="text-btn" onClick={pickImages}>
              Escolher imagens
            </button>
          </p>
        </div>
      </li>
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconPuzzle />
        </span>
        <div>
          <p class="guide-name">Extensão do navegador</p>
          <p class="guide-line">
            Guarde a página aberta com um clique ou Alt+Shift+S.{" "}
            <a href="/aparelhos?guia=extensao" onClick={nav("/aparelhos?guia=extensao")}>
              Instalar a extensão
            </a>
          </p>
        </div>
      </li>
      <li>
        <span class="guide-icon" aria-hidden="true">
          <IconPhone />
        </span>
        <div>
          <p class="guide-name">Compartilhar do celular</p>
          <p class="guide-line">
            Mande links, textos e fotos pela folha Compartilhar. Como ligar no{" "}
            <a href="/aparelhos?guia=android" onClick={nav("/aparelhos?guia=android")}>
              Android
            </a>{" "}
            ou no{" "}
            <a href="/aparelhos?guia=iphone" onClick={nav("/aparelhos?guia=iphone")}>
              iPhone
            </a>
            .
          </p>
        </div>
      </li>
    </ol>
  );
}

/** Estado vazio: título, linha opcional e o que vier dentro (guia, círculo de enviar). */
function Empty({ title, line, status, children }: { title: string; line?: string; status?: boolean; children?: ComponentChildren }) {
  return (
    <div class="empty" role={status ? "status" : undefined}>
      <p class="empty-title">{title}</p>
      {line && <p class="empty-line">{line}</p>}
      {children}
    </div>
  );
}

function restore(c: Card) {
  store.restore(c.id).then(() => {
    toast({ key: `res:${c.id}`, text: "Card restaurado.", action: { label: "Desfazer", run: () => void store.remove(c.id).catch(failed) } });
    refocus(() => document.querySelector<HTMLElement>(".trash-open") ?? document.getElementById("main"));
  }, failed);
}

const TRASH_NOTE = `O que você apaga fica aqui por ${LIMITS.trashDays} dias. Depois disso, some de vez.`;

function Trash() {
  const items = store.trash.value;
  // A regra dos 30 dias vem num aviso ao abrir (e fica no texto para leitor de tela).
  useEffect(() => toast({ key: "trash-note", text: TRASH_NOTE }, 10_000), []);
  return (
    <main id="main" tabIndex={-1} class="page trash">
      <header class="page-head">
        <a class="round-back" href="/" onClick={backTo("/")} aria-label="Voltar para Tudo">
          <IconPrev />
        </a>
        <h1>Lixeira</h1>
        <a class="pill" href="/" onClick={backTo("/")}>
          Sair da lixeira
        </a>
      </header>
      <p class="sr-only">{TRASH_NOTE}</p>
      {items.length === 0 ? (
        <Empty title="A lixeira está vazia." />
      ) : (
        <>
          {/* ponytail: mostra os 500 mais recentes; paginar se a lixeira passar disso */}
          <Grid
            cards={items.slice(0, 500)}
            lead={false}
            resetKey="trash"
            density="compacto"
            render={(c) => (
              <div class="card trash-item" key={c.id} data-kind={c.kind}>
                {/* Antes da face: o anel de foco sai de .trash-open:focus-visible ~ .frame (sem :has). */}
                <button type="button" class="trash-open" aria-label={`Restaurar ${KIND_LABEL[c.kind]}: ${cardText(c).slice(0, 60)}`} onClick={() => restore(c)} />
                <Frame card={c} />
                <Caption card={c} />
                <ImageRetry card={c} name={`${KIND_LABEL[c.kind]}: ${cardText(c).slice(0, 60)}`} />
              </div>
            )}
          />
          <p class="trash-hint" aria-hidden="true">
            <IconCursorCard />
            {TOUCH ? "Toque num card para restaurar" : "Clique num card para restaurar"}
          </p>
        </>
      )}
    </main>
  );
}

const SYNC_ICON = { ok: IconCheck, busy: IconSpin, offline: IconOffline, error: IconAlert };

function SyncStatus() {
  const s = store.status.value;
  const [state, view] = syncView(s);
  // Sessão expirada: entrar de novo. A fila de saída fica no IndexedDB (só sair a apaga) e sobe depois.
  const expired = s.error === "unauthorized";
  const label = expired ? "Sessão expirada" : s.error === "account_mismatch" ? "Abrindo a outra conta…" : view;
  const Icon = SYNC_ICON[state];
  // A sessão pode ter voltado por fora (folha do Google, outra aba): ao voltar ao primeiro plano, tenta de novo.
  useWake(() => void (store.status.peek().error === "unauthorized" && store.sync()));
  return (
    <p class={`sync is-${state}`} title={s.lastSyncAt ? `${label}. Última sincronização: ${absoluteTime(s.lastSyncAt)}` : label}>
      <Icon />
      <span class="sync-text">{label}</span>
      {expired &&
        (standalone() ? (
          // App instalado: o Google pode não devolver a sessão para cá; a folha oferece também o código.
          <button type="button" class="sync-login" popovertarget="relogin">
            Entrar de novo
          </button>
        ) : (
          <a class="sync-login" href="/auth/google">
            Entrar de novo
          </a>
        ))}
    </p>
  );
}

/** Conta da folha de entrada quando a sessão não venceu: voltar para quem deixou itens na fila (gaveta, aviso da troca). */
const target = signal<Left | null>(null);
function relogin(l: Left) {
  target.value = l;
  hide("menu-sheet");
  requestAnimationFrame(() => document.getElementById("relogin")?.showPopover());
}

/**
 * Folha "Entrar de novo" do app instalado: Google ou código, e a fila de saída sobe assim que a sessão volta. Também
 * "Entrar com <outra conta>": o código dela troca o cookie, o sync acusa account_mismatch e o app reabre nela.
 */
function Relogin() {
  const t = store.status.value.error === "unauthorized" ? null : (target.value ?? left.value[0]);
  return (
    <div id="relogin" popover="auto" class="pop relogin" role="dialog" aria-labelledby="relogin-title" tabIndex={-1} onToggle={sheetFocus}>
      <h2 id="relogin-title">{t ? `Entrar com ${t.email}` : "Entrar de novo"}</h2>
      <p>
        {t
          ? `${items(t.pending)} ${t.pending === 1 ? "espera neste aparelho e sobe" : "esperam neste aparelho e sobem"} assim que você entrar.`
          : "Sua sessão terminou. O que está neste aparelho continua aqui e sobe quando você entrar."}
      </p>
      <LoginChoices
        codeFirst
        onCode={async () => {
          hide("relogin");
          await store.sync();
          // Código de outra conta: o sync acusou account_mismatch e o app já está reabrindo nela (sem "Pronto").
          if (!store.status.peek().error) toast({ key: "relogin", text: "Pronto, você entrou de novo." });
        }}
      />
    </div>
  );
}

/** Escolha de arquivos dos seletores do "+": cada um vira card; cancelar não cria nada (V23, V24). */
function pickedFiles(e: Event) {
  const input = e.currentTarget as HTMLInputElement;
  for (const f of input.files ?? []) store.createFile(f).catch(failed);
  input.value = "";
}
const pick = (id: string) => (hide("add-menu"), document.getElementById(id)?.click());

/** Texto colado: endereço vira link; o resto, nota. */
async function saveText(raw: string) {
  const t = raw.trim();
  if (!t) return void toast({ key: "clip-empty", text: "Não há nada copiado para colar." });
  const url = urlFromText(t);
  await (url ? store.createLink(url) : store.createNote(textDoc(t), t));
}
/**
 * "Colar" do "+": no celular, fora de um campo não há onde colar. Imagem copiada vira card de imagem; endereço, link;
 * texto, nota. O iPhone mostra o balão "Colar" do sistema antes de entregar o conteúdo.
 */
async function pasteClipboard() {
  hide("add-menu");
  const clip = navigator.clipboard;
  try {
    if (!clip?.read) return await saveText(await clip.readText());
    for (const item of await clip.read()) {
      const image = item.types.find((t) => t.startsWith("image/"));
      if (image) return void (await store.createFile(new File([await item.getType(image)], "colado", { type: image })));
      if (item.types.includes("text/plain")) return await saveText(await (await item.getType("text/plain")).text());
    }
    await saveText("");
  } catch (e) {
    if ((e as Error | undefined)?.cause === "notified") return; // o store já avisou (formato, tamanho)
    toast({ key: "clip-fail", text: "Não deu para ler o que foi copiado. Toque e segure na nota rápida e escolha Colar." });
  }
}

/**
 * Pílulas Nota · Tirar foto (só tela de toque: abre a câmera direto) · Imagem · Arquivo (PDF, vídeo, áudio) · Link sob o
 * "+" (spec §5.3 F-M4). No celular "Link" abre o campo num segundo passo; no desktop o campo já vem aberto.
 */
function AddMenu() {
  const phone = width.value <= 768;
  const touch = TOUCH;
  const [linking, setLinking] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => void (linking && field.current?.focus()), [linking]);
  return (
    <>
      <div
        id="add-menu"
        popover="auto"
        class="pop add-pop"
        role="dialog"
        aria-label="Adicionar"
        tabIndex={-1}
        onToggle={(e) => {
          if (e.newState === "closed") setLinking(false);
          sheetFocus(e);
        }}
      >
        <button type="button" class="add-pill" onClick={() => (hide("add-menu"), openNote())}>
          Nota
        </button>
        {touch && (
          <button type="button" class="add-pill" onClick={() => pick("add-camera")}>
            Tirar foto
          </button>
        )}
        <button type="button" class="add-pill" onClick={() => pick("add-file")}>
          {phone ? "Foto ou imagem" : "Imagem"}
        </button>
        <button type="button" class="add-pill" onClick={() => pick("add-doc")}>
          Arquivo
        </button>
        {touch && (
          <button type="button" class="add-pill" onClick={() => void pasteClipboard()}>
            Colar
          </button>
        )}
        {phone && !linking ? (
          <button type="button" class="add-pill" onClick={() => setLinking(true)}>
            Link
          </button>
        ) : (
          <form
            noValidate // o aviso é nosso, em português (o balão do navegador recusava "exemplo.com/x")
            onSubmit={(e) => {
              e.preventDefault();
              const input = e.currentTarget.elements.namedItem("url") as HTMLInputElement;
              const url = linkFromInput(input.value);
              if (!url) return toast({ key: "bad-url", text: "Esse endereço não parece um link. Tente algo como exemplo.com/pagina." });
              // Limpa só depois de gravar: se falhar, o endereço continua no campo.
              store.createLink(url).then(() => ((input.value = ""), hide("add-menu")), failed);
            }}
          >
            <label for="add-link">Link</label>
            <input id="add-link" ref={field} name="url" type="url" required placeholder="https://…" autocomplete="off" enterkeyhint="go" />
            <button class="add-pill primary">Guardar link</button>
          </form>
        )}
      </div>
      <input id="add-file" type="file" accept={PHOTO_ACCEPT} multiple hidden onChange={pickedFiles} />
      <input id="add-camera" type="file" accept="image/*" capture="environment" hidden onChange={pickedFiles} />
      <input id="add-doc" type="file" accept={FILE_ACCEPT} multiple hidden onChange={pickedFiles} />
    </>
  );
}

// Grupos da gaveta de atalhos; `true` = combinação (as teclas ganham "+" entre elas).
const HELP: [string, [string[], string, true?][]][] = [
  [
    "Busca",
    [
      [["/"], "Buscar"],
      [["Enter"], "Fixar o termo como filtro"],
    ],
  ],
  [
    "Geral",
    [
      [["N"], "Escrever uma nota"],
      [[MOD, "Enter"], "Guardar a nota", true],
      [[MOD, "V"], "Guardar o link ou a imagem copiada", true],
      [["←", "→"], "Card anterior ou seguinte, no detalhe"],
      [["Esc"], "Fechar ou sair do campo"],
      [["?"], "Mostrar esta lista"],
    ],
  ],
];

/** Fecha ao contrário de como abriu: com data-closing a gaveta sai pela direita e a página volta (CSS); fecha no fim. */
function closeHelp() {
  const d = document.getElementById("help") as HTMLDialogElement | null;
  if (!d?.open || d.hasAttribute("data-closing")) return;
  d.setAttribute("data-closing", "");
  Promise.allSettled(d.getAnimations().map((a) => a.finished)).then(() => d.close());
}

/** Gaveta de atalhos (entra pela direita; ≥ 1025 empurra a página para a esquerda). Esc, × ou clique fora fecham. */
function Help() {
  return (
    <dialog
      id="help"
      class="help"
      aria-labelledby="help-title"
      tabIndex={-1}
      onCancel={(e) => {
        e.preventDefault();
        closeHelp();
      }}
      onClose={(e) => e.currentTarget.removeAttribute("data-closing")}
      onClick={(e) => e.target === e.currentTarget && e.clientX < e.currentTarget.getBoundingClientRect().left && closeHelp()}
    >
      <h2 id="help-title">Atalhos de teclado para ganhar tempo</h2>
      {HELP.map(([group, rows]) => (
        <section key={group}>
          <h3>{group}</h3>
          <dl>
            {rows.map(([keys, what, combo]) => (
              <div class="help-row" key={what}>
                <dt>{what}</dt>
                <dd>
                  {keys.map((k, i) => (
                    <Fragment key={k}>
                      {combo && i > 0 && <span class="plus">+</span>}
                      <kbd>{k}</kbd>
                    </Fragment>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      <button type="button" class="help-close" aria-label="Fechar" onClick={closeHelp}>
        <IconClose />
      </button>
    </dialog>
  );
}

const FOCUSABLE = "button:not([disabled]), [href], input, [tabindex]:not([tabindex='-1'])";
let beforeToast: HTMLElement | null = null; // quem tinha o foco antes de ele entrar nos avisos

/** Botão do aviso some junto com ele: o foco volta para onde estava, senão para o diálogo aberto, senão para a grade. */
function closeToast(t: Toast, run?: () => void) {
  const from = beforeToast;
  run?.();
  dismiss(t.key);
  refocus(() => {
    const d = modal.peek();
    if (from?.isConnected && (!d || d.contains(from))) return from;
    return d ? d.querySelector<HTMLElement>(FOCUSABLE) : document.getElementById("main");
  }, { preventScroll: true });
}

// Envio de arquivos: "Enviando 2 de 5 · 42%" enquanto houver card subindo. O lote cresce se chegarem mais; zera ao
// terminar. Sem conexão nada sobe: o aviso diz que ficou na fila. O % fica fora do leitor de tela (cada número novo
// numa região aria-live seria um anúncio: ~100 por foto); ele diz só a troca de arquivo e o fim.
let batch = 0;
function uploadText(): { text: string; pct?: number } | null {
  const up = store.cards.value.filter((c) => c.ingestState === "uploading");
  const n = up.length;
  batch = n ? Math.max(batch, n) : 0;
  if (!n) return null;
  if (!store.status.value.online) {
    const [one, many] = up.every((c) => c.kind === "image") ? ["foto", "fotos"] : ["arquivo", "arquivos"];
    return { text: n === 1 ? `Sem conexão: 1 ${one} na fila. Sobe quando a conexão voltar.` : `Sem conexão: ${n} ${many} na fila. Sobem quando a conexão voltar.` };
  }
  const pct = up.map((c) => store.progress.value.get(c.id)).find((p) => p !== undefined);
  return { text: `Enviando ${batch - n + 1} de ${batch}`, pct };
}

/** Com um diálogo modal aberto, os avisos vão para dentro dele (portal): por cima e ainda clicáveis. */
function Toasts() {
  const host = modal.value;
  const sending = uploadText();
  const region = (
    <div
      class="toasts"
      role="status"
      aria-live="polite"
      onFocusIn={(e) => {
        const from = e.relatedTarget as HTMLElement | null;
        if (!from || !e.currentTarget.contains(from)) beforeToast = from;
      }}
    >
      {sending && (
        <div class="upload-note">
          {sending.text}
          {sending.pct !== undefined && <span aria-hidden="true">{` · ${sending.pct}%`}</span>}
        </div>
      )}
      {toasts.value.map((t) => (
        <div class="toast" key={t.key} data-key={t.key}>
          <span>{t.text}</span>
          {t.action && (
            <button type="button" class="toast-action" onClick={() => closeToast(t, t.action!.run)}>
              {t.action.label}
            </button>
          )}
          <button type="button" class="toast-x" aria-label="Fechar aviso" onClick={() => closeToast(t)}>
            <IconClose />
          </button>
        </div>
      ))}
    </div>
  );
  return host ? createPortal(region, host) : region;
}
